import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import axios from "axios";

import CustomInput, {
  CustomInputLabel,
} from "src/app/components/CustomInput/CustomInput";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ModalColumn } from "src/app/components/Modal/Modal";
import { ExtensionTypeEnum, RecruitmentFormDetails } from "src/types";
import { CoverImageUploader } from "src/app/components/CoverImageUploader/CoverImageUploader";
import { showToast } from "src/app/methods/showToast";
import { API_URLS } from "src/utils/API_URLS";
import { TextBoxWrapper } from "src/app/components/TextBoxWrapper/TextBoxWrapper";
import {
  WorkspacePermissionsEnum,
  ProjectPermissionsEnum,
} from "src/utils/PermissionsEnums";
import { showErrorToast } from "src/utils/methods";
import ImageUploader from "src/app/components/ImageUploader/ImageUploader";
import { uploadExtensionImageFile as uploadExtensionAssetFile } from "../Buttons/uploadExtensionCover";
import { RecruitmentFormUserData } from "../../types";
import { getExtensionAssetObject } from "../../utils";
import { translateMessage } from "src/app/methods/translateMessage";
import { useIntl } from "react-intl";

const INPUT_FIELD_CALLBACK_DELAY = 2000;

interface RecruitmentFormGeneralProps {
  userData: RecruitmentFormUserData;
  setUserData: Dispatch<SetStateAction<RecruitmentFormUserData>>;
  logoImageFile: File | null;
  setNewLogoImage: (file: File) => void;
  setLogoImageFile: Dispatch<SetStateAction<File | null>>;
  coverImageFile: File | null;
  setNewImage: (file: File) => void;
  setCoverImageFile: Dispatch<SetStateAction<File | null>>;
  recruitmentFormDetails: RecruitmentFormDetails;
  contextData: any;
  extensionUuid?: string;
  extensionType: ExtensionTypeEnum;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  isLogoPhotoRemoved: boolean;
  setIsLogoPhotoRemoved: Dispatch<SetStateAction<boolean>>;
  isCoverPhotoRemoved: boolean;
  setIsCoverPhotoRemoved: Dispatch<SetStateAction<boolean>>;
}

export function RecruitmentFormGeneral({
  userData,
  setUserData,
  logoImageFile,
  setNewLogoImage,
  setLogoImageFile,
  coverImageFile,
  setNewImage,
  setCoverImageFile,
  recruitmentFormDetails,
  contextData,
  extensionUuid,
  extensionType,
  setIsLoading,
  isLogoPhotoRemoved,
  setIsLogoPhotoRemoved,
  isCoverPhotoRemoved,
  setIsCoverPhotoRemoved,
}: RecruitmentFormGeneralProps) {
  const [showUploadLogoModal, setShowUploadLogoModal] =
    useState<boolean>(false);
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState(
    userData.recruitmentFormMetaFields.caption,
  );
  const [description, setDescription] = useState(
    userData.recruitmentFormMetaFields.description,
  );
  const [successMessage, setSuccessMessage] = useState(
    userData.recruitmentFormMetaFields.successMessage,
  );

  const intl = useIntl();

  const handleUpdateBaseField = async (
    wsExtensionUuid: string,
    type: "caption" | "description" | "successMessage",
    value: string,
  ) => {
    try {
      const result = await axios.put(
        API_URLS.updateExtensionDetails.replace(
          ":wsExtensionUuid:",
          wsExtensionUuid,
        ),
        {
          ...userData,
          recruitmentFormMetaFields: {
            ...userData.recruitmentFormMetaFields,
            [type]: value,
          },
          wsAccessLinkCover: null,
          wsAccessLinkLogo: null,
        },
      );
      if (result.statusText === "OK") {
        showToast("success", "Success", "Updated form");
      }
    } catch (err) {
      showToast("error", "Error", "Cannot change form");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadAsset = async (
    wsExtensionUuid: string,
    recruitmentFormUuid: string,
    file: File,
    assetKey: string,
  ) => {
    let realName;
    let updatedUserData = userData;

    try {
      realName = await uploadExtensionAssetFile(
        file,
        contextData.workspaceUuid,
        recruitmentFormUuid,
      );
      if (realName) {
        const { wsAccessLinkLogo, wsAccessLinkCover, ...restOfData } = userData;

        // @ts-ignore
        updatedUserData = {
          ...restOfData,
          wsExtensionType: extensionType,
          [assetKey]: {
            mimeType: file.type,
            name: file.name,
            parentUuid: recruitmentFormUuid,
            realName,
            size: file.size,
          },
        };
        await axios.put(
          API_URLS.updateExtensionDetails.replace(
            ":wsExtensionUuid:",
            wsExtensionUuid,
          ),
          {
            ...updatedUserData,
          },
        );
      }
    } catch (err) {
      console.error(err);
      showErrorToast();
    }
  };

  const handleDeleteRecruitmentFormAsset = async (
    recruitmentFormUuid: string,
    assetType: "logo" | "cover",
  ) => {
    try {
      await axios.delete(
        `${API_URLS.deleteRecruitmentFormAsset.replace(
          ":wsExtensionUuid:",
          recruitmentFormUuid,
        )}?wsExtensionType=${recruitmentFormDetails.type}&assetType=${assetType}`,
      );
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_something_went_wrong"
          defaultMessage="Something went wrong!"
        />,
      );
    }
  };

  useEffect(() => {
    setUserData((prev) => {
      let wsAccessLinkLogo = isCoverPhotoRemoved
        ? null
        : recruitmentFormDetails.wsAccessLinkLogo;
      if (logoImageFile) {
        wsAccessLinkLogo = getExtensionAssetObject(
          logoImageFile,
          recruitmentFormDetails.uuid,
          "",
        );
      }

      let wsAccessLinkCover = isCoverPhotoRemoved
        ? null
        : recruitmentFormDetails.wsAccessLinkCover;
      if (coverImageFile) {
        wsAccessLinkCover = getExtensionAssetObject(
          coverImageFile,
          recruitmentFormDetails.uuid,
          "",
        );
      }
      return {
        ...prev,
        recruitmentFormMetaFields: {
          ...prev.recruitmentFormMetaFields,
          caption,
          description,
          successMessage,
        },
        wsAccessLinkLogo,
        wsAccessLinkCover,
      };
    });

    if (
      !contextData?.permissions?.project?.includes(
        ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
      ) &&
      !contextData?.permissions?.workspace?.includes(
        WorkspacePermissionsEnum.CREATOR_DATABASE_CONFIGURATION,
      )
    )
      return;
    if (!extensionUuid) return;

    const updatedValues = {
      caption,
      description,
      successMessage,
    };

    const prevValues = userData.recruitmentFormMetaFields;

    type MetaField = keyof typeof updatedValues;

    const changedMetaFieldName = (
      Object.keys(updatedValues) as MetaField[]
    ).find((key) => updatedValues[key] !== prevValues[key]);

    if (!changedMetaFieldName) return;

    setIsLoading(true);

    const timerId = setTimeout(() => {
      handleUpdateBaseField(
        extensionUuid,
        changedMetaFieldName,
        updatedValues[changedMetaFieldName],
      );
    }, INPUT_FIELD_CALLBACK_DELAY);

    return () => {
      clearTimeout(timerId);
      setIsLoading(false);
    };
  }, [
    caption,
    description,
    successMessage,
    coverImageFile,
    isCoverPhotoRemoved,
    recruitmentFormDetails,
    extensionUuid,
    contextData,
  ]);

  useEffect(() => {
    if (logoImageFile) {
      setLogoImageUrl(URL.createObjectURL(logoImageFile));
      return;
    }
    if (typeof userData.wsAccessLinkLogo === "string") {
      setLogoImageUrl(userData.wsAccessLinkLogo);
    }
    if (isLogoPhotoRemoved) {
      setLogoImageUrl(null);
    }
  }, [logoImageFile, userData.wsAccessLinkLogo, isLogoPhotoRemoved]);

  useEffect(() => {
    if (coverImageFile) {
      setCoverImageUrl(URL.createObjectURL(coverImageFile));
      return;
    }
    if (typeof userData.wsAccessLinkCover === "string") {
      setCoverImageUrl(userData.wsAccessLinkCover);
    }
    if (isCoverPhotoRemoved) {
      setCoverImageUrl(null);
    }
  }, [coverImageFile, userData.wsAccessLinkCover, isCoverPhotoRemoved]);

  useEffect(() => {
    setCaption(userData.recruitmentFormMetaFields.caption);
    setDescription(userData.recruitmentFormMetaFields.description);

    if (userData.recruitmentFormMetaFields.successMessage) {
      setSuccessMessage(userData.recruitmentFormMetaFields.successMessage);
    } else {
      if (extensionType === ExtensionTypeEnum.RecruitmentForm) {
        setSuccessMessage(
          translateMessage({
            intl,
            id: "ws_thank_you_for_applying_your_submission_has_been_received",
            defaultMessage:
              "Thank you for applying! Your submission has been received.",
          }),
        );
      } else {
        setSuccessMessage(
          translateMessage({
            intl,
            id: "ws_thank_you_for_signing_up_your_profile_has_been_successfully_submitted",
            defaultMessage:
              "Thank you for signing up! Your profile has been successfully submitted.",
          }),
        );
      }
    }
  }, [userData, extensionType]);

  return (
    <>
      <div className="recruitment-form__description">
        <IDHFormattedMessage
          id="ws_recruitment_form_general_description"
          defaultMessage="Create a form that you can then share with people outside of the workspace. Add a brand logo, title and description that will help those you share it with understand the context and needs."
        />
      </div>
      <div className="recruitment-form__general-wrapper">
        <ModalColumn>
          <div className="recruitment-form__title">
            <IDHFormattedMessage
              id="ws_brand_logo"
              defaultMessage="Brand logo"
            />
          </div>
          <CoverImageUploader
            showUploadLogoModal={showUploadLogoModal}
            uploadFunction={(file: File) => {
              setNewLogoImage(file);
              if (
                contextData?.permissions?.project?.includes(
                  ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
                ) ||
                contextData?.permissions?.workspace?.includes(
                  WorkspacePermissionsEnum.CREATOR_DATABASE_CONFIGURATION,
                )
              ) {
                if (extensionUuid && userData.recruitmentFormUuid) {
                  handleUploadAsset(
                    extensionUuid,
                    userData.recruitmentFormUuid,
                    file,
                    "wsAccessLinkLogo",
                  );
                }
              }
              setIsLogoPhotoRemoved(false);
            }}
            closeModal={() => setShowUploadLogoModal(false)}
            coverImageUrl={logoImageUrl}
            openModal={() => setShowUploadLogoModal(true)}
            removeImage={() => {
              if (
                userData.recruitmentFormUuid &&
                (contextData?.permissions?.project?.includes(
                  ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
                ) ||
                  contextData?.permissions?.workspace?.includes(
                    WorkspacePermissionsEnum.CREATOR_DATABASE_CONFIGURATION,
                  ))
              ) {
                setIsLogoPhotoRemoved(true);
                handleDeleteRecruitmentFormAsset(
                  userData.recruitmentFormUuid,
                  "logo",
                );
                setLogoImageUrl(null);
                setLogoImageFile(null);
              }
            }}
          />
        </ModalColumn>
        <ModalColumn>
          <div className="recruitment-form__title">
            <IDHFormattedMessage id="ws_form_info" defaultMessage="Form info" />
          </div>
          <CustomInputLabel htmlFor="title">
            <IDHFormattedMessage id="ws_title" defaultMessage="Title" />
          </CustomInputLabel>
          <CustomInput
            id="title"
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder={translateMessage({
              intl,
              id: "ws_enter_title",
              defaultMessage: "Enter title",
            })}
          />
        </ModalColumn>
        <ModalColumn>
          <div className="recruitment-form__title">
            <IDHFormattedMessage
              id="ws_cover_photo"
              defaultMessage="Cover photo"
            />
          </div>
          <ImageUploader
            imageUrl={coverImageUrl}
            uploadFunction={(file: File) => {
              setNewImage(file);
              if (
                contextData?.permissions?.project?.includes(
                  ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
                ) ||
                contextData?.permissions?.workspace?.includes(
                  WorkspacePermissionsEnum.CREATOR_DATABASE_CONFIGURATION,
                )
              ) {
                if (extensionUuid && userData.recruitmentFormUuid) {
                  handleUploadAsset(
                    extensionUuid,
                    userData.recruitmentFormUuid,
                    file,
                    "wsAccessLinkCover",
                  );
                }
              }
              setIsCoverPhotoRemoved(false);
            }}
            removeFunction={() => {
              if (
                userData.recruitmentFormUuid &&
                (contextData?.permissions?.project?.includes(
                  ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
                ) ||
                  contextData?.permissions?.workspace?.includes(
                    WorkspacePermissionsEnum.CREATOR_DATABASE_CONFIGURATION,
                  ))
              ) {
                setIsCoverPhotoRemoved(true);
                handleDeleteRecruitmentFormAsset(
                  userData.recruitmentFormUuid,
                  "cover",
                );
                setCoverImageUrl(null);
                setCoverImageFile(null);
              }
            }}
          />
        </ModalColumn>
        <ModalColumn>
          <CustomInputLabel htmlFor="description">
            <IDHFormattedMessage
              id="ws_description"
              defaultMessage="Description"
            />
          </CustomInputLabel>

          <TextBoxWrapper
            id="recruitment-form-textbox"
            className="ws-input recruitment-form__text-area"
            value={description || ""}
            onChange={setDescription}
            placeholder={translateMessage({
              intl,
              id: "ws_enter_description",
              defaultMessage: "Enter description",
            })}
          />
        </ModalColumn>

        <ModalColumn>
          <CustomInputLabel htmlFor="successMessage">
            <IDHFormattedMessage
              id="ws_success_message"
              defaultMessage="Success message"
            />
          </CustomInputLabel>

          <TextBoxWrapper
            id="recruitment-form-textbox"
            className="ws-input recruitment-form__text-area"
            value={successMessage || ""}
            onChange={setSuccessMessage}
            placeholder={translateMessage({
              intl,
              id: "ws_enter_success_message",
              defaultMessage: "Enter success message",
            })}
          />
        </ModalColumn>
      </div>
    </>
  );
}
